import { resultForTeam } from "../teamPerformance";
import { shootoutLabel } from "@/features/matches/matchResult";
import { useEffect, useState } from "react";
import { CoachModal } from "@/features/coach/components/CoachModal";
import { useCollection } from "@/features/collections/useCollection";
import {
  CollectionToolbar,
  CollectionPagination,
  PageNavigation,
} from "@/features/collections/CollectionControls";
import {
  Link,
  useNavigate,
  useParams,
  useSearch,
} from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  ArrowUpRight,
  Flag,
  Globe2,
  Handshake,
  Inbox,
  LockKeyhole,
  Play,
  RadioTower,
  Search,
  Trophy,
  WifiOff,
} from "lucide-react";
import {
  ApiError,
  getAppSettings,
  getPublicTeam,
  getPublicTeams,
  getTeamPerformance,
  getTeamResults,
  getLiveStatusForTeam,
} from "@/lib/api";
import {
  publicAppSettingsKey,
  publicHostKey,
  useViewerHostKey,
} from "@/lib/queryClient";
import { useMe } from "@/features/auth/useMe";
import { useDocumentMeta } from "@/lib/useDocumentMeta";
import { TkticSelect } from "@/components/ui/TkticSelect";
import { Button } from "@/components/ui/Button";
import { firstReadySlot } from "@/features/season/seasonHelpers";
import type { PerformanceRecord, Match, TeamPerformance } from "@/lib/types";
import { outcomeLabel } from "@/features/matches/matchCompetition";
import { teamBadgeLabel } from "@/lib/teamBadge";
import { ClubMark } from "@/components/ClubMark";
import { teamRoleLabel } from "@/lib/teamRoleLabel";
export { computeTeamPerformance } from "../teamPerformance";

const VIEWS = [
  ["overview", "Season overview"],
  ["league", "League"],
  ["tournaments", "Tournaments"],
  ["friendlies", "Friendlies"],
  ["matches", "Matches & video"],
] as const;
export function TeamMatchesPage() {
  const { teamSlug } = useParams({ from: "/teams/$teamSlug" });
  const search = useSearch({ from: "/teams/$teamSlug" });
  const navigate = useNavigate({ from: "/teams/$teamSlug" });
  const me = useMe();
  const ready = !(me.isPending && !me.isFetched);
  const teamKey = useViewerHostKey("public-team", { slug: teamSlug });
  const teamQuery = useQuery({
    queryKey: teamKey,
    queryFn: () => getPublicTeam(teamSlug),
    enabled: ready,
    retry: (n, e) =>
      !(e instanceof ApiError && [403, 404].includes(e.status)) && n < 2,
  });
  const publicTeamsQuery = useQuery({
    queryKey: publicHostKey("public-teams"),
    queryFn: getPublicTeams,
    enabled: ready,
    staleTime: 60_000,
  });
  const settingsQuery = useQuery({
    queryKey: publicAppSettingsKey(),
    queryFn: getAppSettings,
    staleTime: 60_000,
  });
  const performanceKey = useViewerHostKey("team-performance", {
    slug: teamSlug,
    season: search.season,
  });
  const performance = useQuery({
    queryKey: performanceKey,
    queryFn: () => getTeamPerformance(teamSlug, search.season),
    enabled: ready && !!teamQuery.data,
  });
  const view = search.view ?? "overview";
  const viewCompetition =
    view === "league"
      ? "league"
      : view === "friendlies"
        ? "friendly"
        : view === "tournaments"
          ? "tournament"
          : undefined;
  const competition = view === "matches" ? search.competition : viewCompetition;
  const selectedSeason = performance.data?.season_id;
  const event = performance.data?.tournaments.find(
    (e) => e.id === search.tournament,
  );
  const [eventStatus, setEventStatus] = useState("");
  useEffect(() => setEventStatus(""), [selectedSeason, teamSlug]);
  const events = useCollection(performance.data?.tournaments ?? [], {
    scope: `events:${teamSlug}:${selectedSeason ?? ""}`,
    text: (e) => `${e.name} ${e.location} ${e.division}`,
    matches: (e, f) => e.outcome === f,
    predicate: (e) => !eventStatus || e.status === eventStatus,
  });
  const resultsKey = useViewerHostKey("team-results", {
    slug: teamSlug,
    season: selectedSeason,
    competition,
    tournament: search.tournament,
    q: search.q,
    status: search.status,
    result: search.result,
    side: search.side,
    recording: search.recording,
    page: search.page,
    limit: search.limit,
  });
  const results = useQuery({
    queryKey: resultsKey,
    queryFn: () =>
      getTeamResults(teamSlug, {
        season_id: selectedSeason,
        competition,
        tournament_id: view === "tournaments" ? search.tournament : undefined,
        q: search.q,
        status: search.status,
        result: search.result,
        side: search.side,
        recording: search.recording,
        page: search.page,
        limit: search.limit ?? 5,
      }),
    enabled: ready && !!teamQuery.data && !!selectedSeason,
  });
  useDocumentMeta({
    title: teamQuery.data ? `${teamQuery.data.name} · tktic` : "Team · tktic",
  });
  const change = (patch: Partial<typeof search>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }) });
  if (!ready || teamQuery.isPending)
    return (
      <main data-testid="team-matches">
        <p role="status">Loading team…</p>
      </main>
    );
  if (teamQuery.isError) {
    const status =
      teamQuery.error instanceof ApiError ? teamQuery.error.status : null;
    const notFound = status === 404;
    const denied = status === 403;
    return (
      <main className="team-unavailable" data-testid="team-matches">
        <header>
          <p className="eyebrow">Team access</p>
          <h1
            data-testid={
              notFound
                ? "team-matches-404"
                : denied
                  ? "team-matches-denied"
                  : "team-matches-error"
            }
          >
            {notFound
              ? "Team not found."
              : denied
                ? "This team isn’t available to your account."
                : "We couldn’t load this right now."}
          </h1>
        </header>
        <section
          className="scorebook-data-state"
          role={denied ? undefined : "alert"}
        >
          <span className="scorebook-state-icon" aria-hidden>
            <Inbox />
          </span>
          <h2>
            {denied || notFound ? "Find your team." : "Try again in a moment."}
          </h2>
          <p>
            {denied || notFound
              ? "Return to the club directory to see the teams available to you. Private teams require membership or an invitation from the club."
              : "Your work is still here. Refresh the page or return to the club directory."}
          </p>
          <Link to="/teams" className="td-empty-secondary">
            Browse teams
          </Link>
        </section>
      </main>
    );
  }
  const team = teamQuery.data;
  const directMembership = me.data?.teams.find((entry) => entry.id === team.id);
  const isOperationalViewer = Boolean(
    !directMembership && (me.data?.is_club_admin || me.data?.is_super_admin),
  );
  const isPublic = Boolean(
    publicTeamsQuery.data?.some((entry) => entry.id === team.id),
  );
  const relationship = directMembership
    ? teamRoleLabel(directMembership.membership_role)
    : isOperationalViewer
      ? "Operational access"
      : isPublic
        ? "Public access"
        : "Guest access";
  const displayClubName =
    settingsQuery.data?.settings?.club_title ||
    me.data?.current_club?.name ||
    "Your club";
  const summary = performance.data?.summary;
  const performanceEmpty = Boolean(
    performance.data &&
    performance.data.authorized_total === 0 &&
    performance.data.tournaments.length === 0 &&
    performance.data.legacy_tournaments.length === 0,
  );
  const controls = (results.data?.authorized_total ?? 0) > 5;
  const completedStructuredTournaments =
    performance.data?.tournaments.filter((t) => t.status === "completed") ?? [];
  const completedLegacyTournaments =
    performance.data?.legacy_tournaments.filter((t) => t.outcome) ?? [];
  const latestStructuredTournament = [...completedStructuredTournaments].sort(
    (a, b) =>
      (b.end_date || b.start_date || "").localeCompare(
        a.end_date || a.start_date || "",
      ),
  )[0];
  const latestLegacyTournament = [...completedLegacyTournaments].sort((a, b) => {
    const latestDate = (group: TeamPerformance["legacy_tournaments"][number]) =>
      group.matches.reduce(
        (latest, match) => (match.date > latest ? match.date : latest),
        "",
      );
    return latestDate(b).localeCompare(latestDate(a));
  })[0];
  const recentLeagueResults =
    performance.data?.recent_league_results ??
    performance.data?.recent_results.filter(
      (match) => (match.competition || "league") === "league",
    ) ??
    [];
  return (
    <main className="team-scorebook" data-testid="team-matches">
      {publicTeamsQuery.isSuccess ? (
        <div className="scorebook-access" data-testid="team-access-summary">
          <span className={`td-visibility ${isPublic ? "public" : "private"}`}>
            {isPublic ? <Globe2 aria-hidden /> : <LockKeyhole aria-hidden />}
            {isPublic ? "Public" : "Private"}
          </span>
          <span>{relationship}</span>
          <span aria-hidden>·</span>
          <span>
            {isPublic
              ? "Public results and recordings"
              : "Available to authorized team members and permitted guests"}
          </span>
        </div>
      ) : null}
      <Link
        to="/teams"
        className="inline-flex items-center gap-2 text-sm text-ink-muted"
      >
        <ArrowLeft size={16} />
        All teams
      </Link>
      <header className="scorebook-header">
        <div className="scorebook-identity">
          <span className="scorebook-team-mark" aria-hidden>
            {teamBadgeLabel(team.name)}
          </span>
          <div>
            <p className="eyebrow scorebook-club-label">
              <ClubMark
                name={displayClubName}
                imageUrl={settingsQuery.data?.assets?.favicon_url}
                fallbackImageUrl={settingsQuery.data?.assets?.club_logo_url}
                className="scorebook-club-mark"
              />
              {displayClubName}
            </p>
            <h1 data-testid="team-matches-name">{team.name}</h1>
            <p className="text-ink-muted">
              The season, the results, the moments.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          {performance.data ? (
            <label className="grid gap-1 text-xs">
              Season
              <TkticSelect
                ariaLabel="Season"
                value={selectedSeason ?? ""}
                options={performance.data.seasons.map((s) => ({
                  value: s.id,
                  label: s.name,
                }))}
                onChange={(season) =>
                  change({
                    season,
                    tournament: undefined,
                    q: undefined,
                    competition: undefined,
                    status: undefined,
                    result: undefined,
                    side: undefined,
                    recording: undefined,
                    page: 1,
                  })
                }
              />
            </label>
          ) : null}
          <LiveCta teamSlug={teamSlug} seasonId={selectedSeason} />
        </div>
      </header>
      <nav className="scorebook-tabs" aria-label="Team performance">
        {VIEWS.map(([id, label]) => (
          <Link
            key={id}
            to="/teams/$teamSlug"
            params={{ teamSlug }}
            search={{ season: search.season, view: id }}
            aria-current={view === id ? "page" : undefined}
          >
            {label}
          </Link>
        ))}
      </nav>
      {performance.isError ? (
        <TeamPerformanceState
          kind="error"
          onRetry={() => void performance.refetch()}
        />
      ) : performance.isPending ? (
        <TeamPerformanceState kind="loading" />
      ) : performanceEmpty ? (
        <TeamPerformanceState kind="empty" />
      ) : null}
      {summary &&
      performance.data &&
      !performanceEmpty &&
      view === "overview" ? (
        <>
          <div className="scorebook-overview">
            <section className="scorebook-hero">
              <div className="scorebook-hero-heading">
                <div>
                  <p className="eyebrow">League season</p>
                  <h2>Your league season.</h2>
                </div>
                <Link
                  to="/teams/$teamSlug"
                  params={{ teamSlug }}
                  search={{ season: selectedSeason, view: "league" }}
                >
                  League performance <ArrowUpRight size={16} />
                </Link>
              </div>
              <p className="scorebook-standings-note">
                Based on {team.name}’s completed league matches.
              </p>
              <div className="scorebook-season-record">
                <strong>{summary.league.played}</strong>
                <span>
                  completed matches
                  <br />
                  {summary.league.won}W · {summary.league.drawn}D ·{" "}
                  {summary.league.lost}L
                </span>
              </div>
              <div className="scorebook-hero-metrics">
                <div>
                  <strong>{summary.league.points}</strong>
                  <span>Points</span>
                </div>
                <div>
                  <strong>
                    {summary.league.goal_difference > 0 ? "+" : ""}
                    {summary.league.goal_difference}
                  </strong>
                  <span>Goal difference</span>
                </div>
                <div>
                  <strong>{summary.league.goals_for}</strong>
                  <span>Goals for</span>
                </div>
                <RecentForm
                  matches={recentLeagueResults}
                  team={team}
                />
              </div>
            </section>
            <section className="scorebook-panel scorebook-next">
              <p className="eyebrow">Next match</p>
              {performance.data.next_fixture ? (
                <ResultRow
                  match={performance.data.next_fixture}
                  teamSlug={teamSlug}
                  seasonId={selectedSeason}
                  team={team}
                />
              ) : (
                <>
                  <h2>The next fixture is still to come.</h2>
                  <p className="text-sm text-ink-muted">
                    No upcoming scheduled fixture.
                  </p>
                </>
              )}
            </section>
          </div>
          <div className="scorebook-season-links">
            <Link
              to="/teams/$teamSlug"
              params={{ teamSlug }}
              search={{ season: selectedSeason, view: "tournaments" }}
            >
              <Trophy />
              <span>
                <strong>
                  {completedStructuredTournaments.length +
                    completedLegacyTournaments.length}{" "}
                  tournaments completed
                </strong>
                <small>
                  {summary.tournament.played} matches played ·{" "}
                  {summary.tournament.won}W · {summary.tournament.drawn}D ·{" "}
                  {summary.tournament.lost}L
                </small>
              </span>
              <ArrowUpRight />
            </Link>
            <Link
              to="/teams/$teamSlug"
              params={{ teamSlug }}
              search={{ season: selectedSeason, view: "friendlies" }}
            >
              <Handshake />
              <span>
                <strong>{summary.friendly.played} friendlies played</strong>
                <small>
                  {summary.friendly.won}W · {summary.friendly.drawn}D ·{" "}
                  {summary.friendly.lost}L · {summary.friendly.goals_for} goals
                  scored
                </small>
              </span>
              <ArrowUpRight />
            </Link>
          </div>
          <section className="scorebook-panel">
            <div className="scorebook-section-heading">
              <div>
                <p className="eyebrow">Latest tournament finish</p>
                <h2>A season beyond the league.</h2>
              </div>
              <Link
                to="/teams/$teamSlug"
                params={{ teamSlug }}
                search={{ season: selectedSeason, view: "tournaments" }}
              >
                Find a tournament
              </Link>
            </div>
            {latestStructuredTournament ? (
              <button
                className="scorebook-event"
                onClick={() =>
                  change({
                    view: "tournaments",
                    tournament: latestStructuredTournament.id,
                    page: 1,
                  })
                }
              >
                <span className="scorebook-event-icon">
                  <Trophy />
                </span>
                <span>
                  <strong>{latestStructuredTournament.name}</strong>
                  <small>
                    {[
                      latestStructuredTournament.start_date,
                      latestStructuredTournament.end_date,
                      latestStructuredTournament.location,
                      latestStructuredTournament.division,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </small>
                </span>
                <span>
                  <strong>
                    {latestStructuredTournament.outcome
                      ? outcomeLabel(latestStructuredTournament.outcome)
                      : "Completed"}
                  </strong>
                  <small>
                    {latestStructuredTournament.summary?.played ?? 0} matches
                    played
                  </small>
                </span>
                <ArrowUpRight />
              </button>
            ) : latestLegacyTournament ? (
              <Link
                className="scorebook-event"
                to="/teams/$teamSlug"
                params={{ teamSlug }}
                search={{ season: selectedSeason, view: "tournaments" }}
              >
                <span className="scorebook-event-icon">
                  <Trophy />
                </span>
                <span>
                  <strong>{latestLegacyTournament.name}</strong>
                  <small>
                    {legacyTournamentDates(latestLegacyTournament.matches)}
                  </small>
                </span>
                <span>
                  <strong>
                    {latestLegacyTournament.outcome
                      ? outcomeLabel(latestLegacyTournament.outcome)
                      : "Completed"}
                  </strong>
                  <small>
                    {latestLegacyTournament.summary.played} matches played
                  </small>
                </span>
                <ArrowUpRight />
              </Link>
            ) : (
              <p className="text-sm text-ink-muted">
                No confirmed tournament finish this season.
              </p>
            )}
          </section>
          <section className="scorebook-panel">
            <div className="scorebook-section-heading">
              <div>
                <p className="eyebrow">Across all competitions</p>
                <h2>Recent results</h2>
              </div>
              <Link
                to="/teams/$teamSlug"
                params={{ teamSlug }}
                search={{ season: selectedSeason, view: "matches" }}
              >
                All matches <ArrowUpRight size={16} />
              </Link>
            </div>
            {performance.data.recent_results.length ? (
              [...performance.data.recent_results]
                .reverse()
                .map((m) => (
                  <ResultRow
                    key={m.id}
                    match={m}
                    teamSlug={teamSlug}
                    seasonId={selectedSeason}
                    team={team}
                  />
                ))
            ) : (
              <p className="text-sm text-ink-muted">
                No confirmed results this season.
              </p>
            )}
          </section>
        </>
      ) : null}
      {summary &&
      !performanceEmpty &&
      (view === "league" || view === "friendlies") ? (
        <>
          <section className="scorebook-panel">
            <div className="scorebook-section-heading">
              <div>
                <p className="eyebrow">
                  {view === "league"
                    ? "League competition"
                    : "Preparation & practice"}
                </p>
                <h2>
                  {view === "league"
                    ? "League performance"
                    : "Friendly match performance"}
                </h2>
              </div>
              {view === "league" ? (
                <span className="scorebook-points">
                  <strong>{summary.league.points}</strong> points
                </span>
              ) : null}
            </div>
            <Metrics
              value={view === "league" ? summary.league : summary.friendly}
            />
          </section>
        </>
      ) : null}
      {!performanceEmpty && view === "tournaments" ? (
        <>
          {search.tournament && !event ? (
            <section className="scorebook-panel">
              <h2>Tournament unavailable</h2>
              <p>This tournament is unavailable in this season.</p>
              <Button
                variant="ghost"
                onClick={() => change({ tournament: undefined, page: 1 })}
              >
                All tournaments
              </Button>
            </section>
          ) : event ? (
            <>
              <Button
                variant="ghost"
                className="scorebook-tournament-back"
                onClick={() => change({ tournament: undefined, page: 1 })}
              >
                <ArrowLeft size={16} /> All tournaments
              </Button>
              <section className="scorebook-tournament-hero">
                <div>
                  <p className="eyebrow">
                    {event.status?.replaceAll("_", " ")} ·{" "}
                    {event.division || "Tournament"}
                  </p>
                  <h2>{event.name}</h2>
                  <p>
                    {event.start_date || "Dates not announced"}
                    {event.end_date ? ` – ${event.end_date}` : ""}
                    {event.location ? ` · ${event.location}` : ""}
                  </p>
                  {event.competition_format ? (
                    <span>{event.competition_format}</span>
                  ) : null}
                </div>
                <div className="scorebook-tournament-finish">
                  <Trophy aria-hidden />
                  <span>
                    {event.status === "completed"
                      ? "Final result"
                      : event.status === "upcoming"
                        ? "Tournament status"
                        : "Progress so far"}
                  </span>
                  <strong>
                    {event.outcome
                      ? outcomeLabel(event.outcome)
                      : event.status?.replaceAll("_", " ") || "Scheduled"}
                  </strong>
                </div>
              </section>
              {event.summary ? (
                <section className="scorebook-panel">
                  <div className="scorebook-section-heading">
                    <h2>{team.name}’s tournament record</h2>
                    <span className="text-sm text-ink-muted">
                      {event.summary.played} completed{" "}
                      {event.summary.played === 1 ? "match" : "matches"}
                    </span>
                  </div>
                  <Metrics value={event.summary} />
                </section>
              ) : null}
            </>
          ) : (
            <section className="scorebook-panel scorebook-tournament-directory">
              <div className="scorebook-section-heading">
                <div>
                  <p className="eyebrow">
                    {performance.data?.seasons.find(
                      (season) => season.id === selectedSeason,
                    )?.name ?? "Current"}{" "}
                    tournament season
                  </p>
                  <h2>Find the weekend. See the whole story.</h2>
                  <p className="text-sm text-ink-muted">
                    Tournament finishes, every round and the moments you can
                    watch again.
                  </p>
                </div>
                <div className="scorebook-tournament-total">
                  <strong>
                    {performance.data?.tournaments.filter(
                      (t) => t.status === "completed",
                    ).length ?? 0}
                  </strong>
                  <span>
                    completed
                    <br />
                    tournaments
                  </span>
                </div>
              </div>
              <div className="scorebook-tournament-summary">
                <span>
                  {performance.data?.tournaments.filter(
                    (t) => t.status === "upcoming",
                  ).length ?? 0}{" "}
                  upcoming
                </span>
                <span>
                  {performance.data?.tournaments.filter(
                    (t) => t.status === "in_progress",
                  ).length ?? 0}{" "}
                  in progress
                </span>
                <span>{summary?.tournament.played ?? 0} matches played</span>
                <strong>
                  {summary?.tournament.won ?? 0}W ·{" "}
                  {summary?.tournament.drawn ?? 0}D ·{" "}
                  {summary?.tournament.lost ?? 0}L
                </strong>
              </div>
              <CollectionToolbar
                collection={events}
                label="tournaments"
                filters={[
                  { value: "", label: "All" },
                  { value: "champions", label: "Champions" },
                  { value: "finalist", label: "Finalists" },
                  { value: "third_place", label: "Third place" },
                  { value: "semi_finalist", label: "Semi-finalists" },
                  { value: "group_stage", label: "Group stage" },
                ]}
                filterLabel="Final result"
                extraFilters={[
                  {
                    label: "Status",
                    ariaLabel: "Tournament status",
                    value: eventStatus,
                    clearValue: "",
                    onChange: setEventStatus,
                    options: [
                      { value: "", label: "All" },
                      { value: "upcoming", label: "Upcoming" },
                      { value: "in_progress", label: "In progress" },
                      { value: "completed", label: "Completed" },
                    ],
                  },
                ]}
              />
              {events.items.map((t) => (
                <button
                  key={t.id}
                  className="scorebook-event"
                  onClick={() =>
                    change({ tournament: t.id, page: 1, q: undefined })
                  }
                >
                  <span className="scorebook-event-icon">
                    <Trophy />
                  </span>
                  <span>
                    <strong>{t.name}</strong>
                    <small>
                      {[
                        t.start_date || "Dates not announced",
                        t.location,
                        t.division,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </small>
                  </span>
                  <span>
                    {t.outcome
                      ? outcomeLabel(t.outcome)
                      : t.status?.replaceAll("_", " ")}
                    <small>{t.match_count ?? 0} matches</small>
                  </span>
                  <ArrowUpRight size={18} />
                </button>
              ))}
              {!performance.data?.tournaments.length ? (
                <p>No tournaments have been added for this season.</p>
              ) : events.filteredTotal === 0 ? (
                <p>No tournaments match your filters.</p>
              ) : null}
              <CollectionPagination collection={events} />
            </section>
          )}
        </>
      ) : null}
      {!performanceEmpty && view === "tournaments" && !search.tournament
        ? performance.data?.legacy_tournaments?.map((group) => (
            <LegacyTournament
              key={group.name}
              group={group}
              teamSlug={teamSlug}
              team={team}
            />
          ))
        : null}
      {performance.data &&
      !performanceEmpty &&
      view !== "overview" &&
      !(view === "tournaments" && !event) ? (
        <section className="scorebook-panel">
          <div className="scorebook-section-heading">
            <div>
              {event ? (
                <p className="eyebrow">Every round, in order</p>
              ) : view === "matches" ? (
                <p className="eyebrow">Every competition</p>
              ) : null}
              <h2>
                {event
                  ? event.status === "upcoming"
                    ? "Tournament fixtures"
                    : "Our tournament run"
                  : view === "league"
                    ? "League fixtures & results"
                    : view === "friendlies"
                      ? "Friendly fixtures & results"
                      : "Matches & video"}
              </h2>
              {view === "matches" ? (
                <p className="text-sm text-ink-muted">
                  Results, upcoming fixtures and recordings for the selected
                  season.
                </p>
              ) : null}
            </div>
            {event ? (
              <span className="scorebook-event-status">
                {event.status?.replaceAll("_", " ") || "Scheduled"}
              </span>
            ) : null}
          </div>
          {controls ? (
            <div
              className="collection-toolbar"
              data-testid="team-matches-filter"
            >
              <label className="collection-search">
                <Search aria-hidden className="collection-search-icon" />
                <span>Search</span>
                <input
                  type="search"
                  data-testid="team-matches-search"
                  aria-label="Search matches"
                  className="w-full !pl-10"
                  placeholder="Opponent, event, round or date"
                  value={search.q ?? ""}
                  onChange={(e) => change({ q: e.target.value, page: 1 })}
                />
              </label>
              {view === "matches" ? (
                <label className="collection-filter">
                  <span>Competition</span>
                  <TkticSelect
                    ariaLabel="Competition"
                    value={search.competition ?? ""}
                    onChange={(value) =>
                      change({
                        competition: (value ||
                          undefined) as typeof search.competition,
                        page: 1,
                      })
                    }
                    options={[
                      { value: "", label: "All" },
                      { value: "league", label: "League" },
                      { value: "tournament", label: "Tournament" },
                      { value: "friendly", label: "Friendly" },
                    ]}
                  />
                </label>
              ) : null}
              <label className="collection-filter">
                <span>Result</span>
                <TkticSelect
                  ariaLabel="Match result"
                  value={search.result ?? ""}
                  onChange={(value) =>
                    change({
                      result: (value || undefined) as typeof search.result,
                      page: 1,
                    })
                  }
                  options={[
                    { value: "", label: "All" },
                    { value: "won", label: "Won" },
                    { value: "drawn", label: "Drawn" },
                    { value: "lost", label: "Lost" },
                    { value: "upcoming", label: "Upcoming" },
                    { value: "won_on_penalties", label: "Won on penalties" },
                    { value: "lost_on_penalties", label: "Lost on penalties" },
                  ]}
                />
              </label>
              <label className="collection-filter">
                <span>Recording</span>
                <TkticSelect
                  ariaLabel="Recording availability"
                  value={search.recording ?? ""}
                  onChange={(value) =>
                    change({
                      recording: (value ||
                        undefined) as typeof search.recording,
                      page: 1,
                    })
                  }
                  options={[
                    { value: "", label: "All" },
                    { value: "available", label: "Available" },
                    { value: "unavailable", label: "Unavailable" },
                  ]}
                />
              </label>
              {search.q ||
              search.competition ||
              search.status ||
              search.result ||
              search.side ||
              search.recording ? (
                <Button
                  variant="ghost"
                  onClick={() =>
                    change({
                      q: undefined,
                      competition: undefined,
                      status: undefined,
                      result: undefined,
                      side: undefined,
                      recording: undefined,
                      page: 1,
                    })
                  }
                >
                  Clear filters
                </Button>
              ) : null}
            </div>
          ) : null}
          {results.isPending ? (
            <p role="status">Loading matches…</p>
          ) : results.isError ? (
            <p role="alert">
              Couldn’t load results.{" "}
              <button onClick={() => void results.refetch()}>Retry</button>
            </p>
          ) : !results.data.matches.length ? (
            <p className="text-ink-muted">
              {controls &&
              Boolean(
                search.q ||
                search.competition ||
                search.status ||
                search.result ||
                search.side ||
                search.recording,
              )
                ? "No matches match your filters."
                : "No fixtures or results yet."}
            </p>
          ) : (
            results.data.matches.map((match) => (
              <ResultRow
                key={match.id}
                match={match}
                teamSlug={teamSlug}
                seasonId={selectedSeason}
                team={team}
              />
            ))
          )}
          {controls && results.data ? (
            <div className="collection-pagination">
              <span>
                {results.data.total} of {results.data.authorized_total} matches
              </span>
              <div className="flex items-center gap-2">
                <span>Per page</span>
                <TkticSelect
                  ariaLabel="Rows per page"
                  value={String(search.limit ?? 5)}
                  options={[5, 10, 25, 50].map((n) => ({
                    value: String(n),
                    label: String(n),
                  }))}
                  onChange={(v) => change({ limit: Number(v), page: 1 })}
                />
                <PageNavigation
                  page={results.data.page}
                  pages={Math.max(
                    1,
                    Math.ceil(results.data.total / results.data.limit),
                  )}
                  onChange={(page) => change({ page })}
                />
              </div>
            </div>
          ) : null}
        </section>
      ) : null}
    </main>
  );
}

function RecentForm({
  matches,
  team,
}: {
  matches: Match[];
  team: { id: string; name: string };
}) {
  const visible = matches.slice(-5);
  return (
    <div className="scorebook-recent-form">
      <span>
        {visible.length ? `Last ${visible.length} · oldest first` : "Recent form"}
      </span>
      {visible.length ? (
        <div aria-label="Recent league form, oldest to newest">
          {visible.map((match) => {
            const result = resultForTeam(match, team.id, team.name);
            const home =
              match.home_team_id === team.id ||
              (!match.home_team_id &&
                !match.away_team_id &&
                match.home_team.trim().toLowerCase() ===
                  team.name.trim().toLowerCase());
            const opponent = home ? match.away_team : match.home_team;
            const ours = home ? match.score_home : match.score_away;
            const theirs = home ? match.score_away : match.score_home;
            const word =
              result === "W" ? "Win" : result === "L" ? "Loss" : "Draw";
            return (
              <span
                key={match.id}
                className="scorebook-form-result"
                data-result={result ?? undefined}
                title={`${shortMatchDate(match.date)} · ${word} against ${opponent}, ${ours}–${theirs}`}
              >
                <span aria-hidden>{result}</span>
                <span className="sr-only">
                  {word} against {opponent}, {ours} to {theirs}
                </span>
              </span>
            );
          })}
        </div>
      ) : (
        <small>No league results yet</small>
      )}
    </div>
  );
}

function TeamPerformanceState({
  kind,
  onRetry,
}: {
  kind: "loading" | "error" | "empty";
  onRetry?: () => void;
}) {
  if (kind === "loading") {
    return (
      <section className="scorebook-data-state loading" role="status">
        <p className="eyebrow">Loading your team…</p>
        <span />
        <span />
        <span />
      </section>
    );
  }
  const error = kind === "error";
  return (
    <section
      className="scorebook-data-state"
      role={error ? "alert" : undefined}
    >
      <span className="scorebook-state-icon" aria-hidden>
        <Inbox />
      </span>
      <h2>
        {error
          ? "We couldn’t load this right now."
          : "A new season starts here."}
      </h2>
      <p>
        {error
          ? "Your work is still here. Try loading this view again."
          : "Results and competition details will appear as matches are added."}
      </p>
      {error && onRetry ? <Button onClick={onRetry}>Try again</Button> : null}
    </section>
  );
}

function Metrics({ value }: { value: PerformanceRecord }) {
  return (
    <>
      <div className="scorebook-metrics">
        {(
          [
            ["Played", value.played],
            ["Won", value.won],
            ["Drawn", value.drawn],
            ["Lost", value.lost],
            ["Goals for", value.goals_for],
            ["Goals against", value.goals_against],
          ] as const
        ).map(([label, n]) => (
          <div key={label}>
            <strong>{n}</strong>
            <span>{label}</span>
          </div>
        ))}
      </div>
      <p className="text-sm text-ink-muted">
        {value.clean_sheets} clean sheets ·{" "}
        {value.goals_per_match == null ? "—" : value.goals_per_match.toFixed(1)}{" "}
        goals per match ·{" "}
        {value.points_per_match == null
          ? "—"
          : value.points_per_match.toFixed(1)}{" "}
        points per match
        {value.pk_won || value.pk_lost
          ? ` · Shootouts: ${value.pk_won} won, ${value.pk_lost} lost`
          : ""}
      </p>
    </>
  );
}

function shortMatchDate(value: string): string {
  if (!value) return "Date to be confirmed";
  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en", {
    day: "numeric",
    month: "short",
  }).format(date);
}

function legacyTournamentDates(matches: Match[]): string {
  const dates = matches
    .map((match) => match.date)
    .filter(Boolean)
    .sort();
  if (!dates.length) return "Dates not confirmed";
  const first = shortMatchDate(dates[0]);
  const last = shortMatchDate(dates[dates.length - 1]);
  return first === last ? first : `${first} – ${last}`;
}

function competitionLabel(match: Match): string {
  if (match.tournament_name) return match.tournament_name;
  return match.competition.replace(/^./, (letter) => letter.toUpperCase());
}

function ResultRow({
  match,
  teamSlug,
  seasonId,
  team,
}: {
  match: Match;
  teamSlug: string;
  seasonId?: string;
  team: { id: string; name: string };
}) {
  const [details, setDetails] = useState(false);
  const slot = firstReadySlot(match);
  const outcome = resultForTeam(match, team.id, team.name);
  const complete =
    match.match_status === "completed" &&
    match.score_home != null &&
    match.score_away != null;
  const shootout = shootoutLabel(match);
  const resultLabel =
    outcome === "W"
      ? "Win"
      : outcome === "L"
        ? "Loss"
        : outcome === "D"
          ? "Draw"
          : null;
  return (
    <>
      <article className="scorebook-match">
        <div>
          <strong>{shortMatchDate(match.date)}</strong>
          <small>
            {[match.time, match.location].filter(Boolean).join(" · ")}
          </small>
        </div>
        <div>
          <strong>
            {match.home_team} vs {match.away_team}
          </strong>
          <small>
            {[match.competition, match.tournament_name, match.stage]
              .filter(Boolean)
              .join(" · ")}
          </small>
        </div>
        <div className="scorebook-score">
          {outcome ? (
            <span
              className="scorebook-outcome"
              data-result={outcome}
              aria-label={
                outcome === "W" ? "Win" : outcome === "L" ? "Loss" : "Draw"
              }
            >
              {outcome}
            </span>
          ) : null}
          {complete ? (
            <>
              <strong>
                {match.score_home} – {match.score_away}
              </strong>
              {shootoutLabel(match) ? (
                <small>{shootoutLabel(match)}</small>
              ) : null}
            </>
          ) : (
            <span>{match.match_status ?? "Scheduled"}</span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <button
            className="text-sm text-accent-text"
            onClick={() => setDetails(true)}
          >
            {complete ? "Details" : "Match details"}
          </button>
          {slot ? (
            <Link
              to="/match/$slug/$slot"
              params={{ slug: match.slug, slot }}
              search={{ from: teamSlug, season: seasonId }}
              className="scorebook-watch"
            >
              Watch <ArrowUpRight size={15} />
            </Link>
          ) : null}
        </div>
      </article>
      {details ? (
        <CoachModal
          title={complete ? "Match result" : "Upcoming match"}
          size="wide"
          bodyClassName="scorebook-match-dialog-body"
          footerClassName="!bg-surface"
          onClose={() => setDetails(false)}
          footer={
            <div className="scorebook-match-dialog-actions">
              {match.tournament_id ? (
                <Link
                  to="/teams/$teamSlug"
                  params={{ teamSlug }}
                  search={{
                    season: match.season_id ?? undefined,
                    view: "tournaments",
                    tournament: match.tournament_id,
                  }}
                  className="scorebook-match-dialog-event"
                >
                  View tournament results
                </Link>
              ) : (
                <span />
              )}
              <Button variant="ghost" onClick={() => setDetails(false)}>
                Done
              </Button>
            </div>
          }
        >
          <div className="scorebook-match-detail">
            <section aria-label="Match details">
              <div>
                <p className="eyebrow">
                  {competitionLabel(match)} · {match.stage || "Match"}
                </p>
                <h2>
                  {match.home_team} <span>vs.</span> {match.away_team}
                </h2>
                <p className="scorebook-match-detail-meta">
                  {shortMatchDate(match.date)}
                  {match.time ? ` · ${match.time}` : ""}
                  {match.location
                    ? ` · ${match.location}`
                    : " · Venue to be confirmed"}
                </p>
              </div>
              {complete ? (
                <>
                  <p
                    className="scorebook-match-detail-score"
                    aria-label={`Match score: ${match.home_team} ${match.score_home}, ${match.away_team} ${match.score_away}`}
                  >
                    {match.score_home} <span>–</span> {match.score_away}
                  </p>
                  <div className="scorebook-match-detail-result">
                    {resultLabel ? (
                      <span data-result={outcome}>{resultLabel}</span>
                    ) : null}
                    <small>
                      Match score · {match.home_team} – {match.away_team}
                    </small>
                  </div>
                </>
              ) : (
                <span className="scorebook-upcoming-pill">Upcoming</span>
              )}
            </section>
            <aside aria-label="Recording and match outcome">
              {shootout ? (
                <div className="scorebook-shootout-detail">
                  <Flag aria-hidden />
                  <div>
                    <strong>{shootout}</strong>
                    <p>
                      The penalty tally is separate from the {match.score_home}–
                      {match.score_away} match score.
                    </p>
                  </div>
                </div>
              ) : null}
              <div className="scorebook-recording-inset">
                <h3>{slot ? "Match recording" : "Recording"}</h3>
                <p>
                  {slot
                    ? "Watch the available match recording."
                    : complete
                      ? "No recording is available for this match. The result is still included in team performance."
                      : "Recording availability will appear after the match."}
                </p>
                {slot ? (
                  <Link
                    to="/match/$slug/$slot"
                    params={{ slug: match.slug, slot }}
                    search={{ from: teamSlug, season: seasonId }}
                    className="scorebook-recording-action"
                  >
                    <Play aria-hidden /> Watch match
                  </Link>
                ) : null}
              </div>
            </aside>
          </div>
        </CoachModal>
      ) : null}
    </>
  );
}

function LiveCta({
  teamSlug,
  seasonId,
}: {
  teamSlug: string;
  seasonId?: string;
}) {
  const liveQueryKey = useViewerHostKey("live-status-team", {
    slug: teamSlug,
  });
  const liveQuery = useQuery({
    queryKey: liveQueryKey,
    queryFn: () => getLiveStatusForTeam(teamSlug),
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });
  const isLive = Boolean(
    liveQuery.data?.enabled && liveQuery.data?.active && liveQuery.data?.ready,
  );

  if (!isLive) {
    return (
      <span
        data-testid="team-matches-live-cta"
        data-live="false"
        aria-disabled="true"
        className="scorebook-live-cta"
      >
        <WifiOff aria-hidden />
        Not live
      </span>
    );
  }

  return (
    <Link
      to="/teams/$teamSlug/live"
      params={{ teamSlug }}
      search={{ season: seasonId }}
      data-testid="team-matches-live-cta"
      data-live="true"
      className="scorebook-live-cta"
    >
      <RadioTower aria-hidden />
      Watch live
    </Link>
  );
}

function LegacyTournament({
  group,
  teamSlug,
  team,
}: {
  group: TeamPerformance["legacy_tournaments"][number];
  teamSlug: string;
  team: { id: string; name: string };
}) {
  const collection = useCollection(group.matches, {
    scope: `legacy-event:${teamSlug}:${group.name}`,
    text: (m) => `${m.home_team} ${m.away_team} ${m.stage ?? ""}`,
  });
  return (
    <section className="scorebook-panel" data-testid="team-matches-tournament">
      <h2>{group.name}</h2>
      <p className="text-xs text-ink-muted">
        Legacy match grouping — event details have not been confirmed.
      </p>
      {group.outcome ? (
        <p data-testid="team-matches-tournament-outcome">
          {outcomeLabel(group.outcome)}
        </p>
      ) : null}
      <div data-testid="team-matches-tournament-record">
        <Metrics value={group.summary} />
      </div>
      <CollectionToolbar collection={collection} label="tournament matches" />
      {collection.items.map((m) => (
        <ResultRow
          key={m.id}
          match={m}
          teamSlug={teamSlug}
          seasonId={m.season_id ?? undefined}
          team={team}
        />
      ))}
      <CollectionPagination collection={collection} />
    </section>
  );
}
